import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, ShieldCheck, ShieldOff } from "lucide-react";
import { useState } from "react";

import { PageHeader } from "@/components/PageHeader";
import { MembersApi, ProjectsApi, UsersApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export function UsersPage() {
  const me = useAuth((s) => s.user);
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: UsersApi.list });
  const projects = useQuery({ queryKey: ["projects"], queryFn: ProjectsApi.list });
  // user_id of the row currently showing the "Add to project" picker (null = none open)
  const [addingForUserId, setAddingForUserId] = useState<number | null>(null);

  const toggleAdmin = useMutation({
    mutationFn: ({ id, makeAdmin }: { id: number; makeAdmin: boolean }) =>
      UsersApi.update(id, { is_superuser: makeAdmin }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
  });

  const addToProject = useMutation({
    mutationFn: ({ projectId, username }: { projectId: number; username: string }) =>
      MembersApi.add(projectId, username),
    onSuccess: (_data, vars) => {
      setAddingForUserId(null);
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["members", vars.projectId] });
    },
  });

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage who has admin privileges. Admins can create projects and access every project's settings."
        actions={
          <button
            type="button"
            className="btn-ghost"
            onClick={() => users.refetch()}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        }
      />

      <div className="px-4 py-4 md:px-8 md:py-6">
        {toggleAdmin.isError && (
          <div className="mb-4 rounded-md border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
            {(toggleAdmin.error as { response?: { data?: { detail?: string } } } | undefined)?.response?.data?.detail
              ?? "Failed to update user"}
          </div>
        )}

        <div className="card overflow-hidden">
          {users.isLoading && <div className="p-6 text-sm text-text-muted">Loading…</div>}
          {users.isError && (
            <div className="p-6 text-sm text-status-error">
              Failed to load users. Are you signed in as an admin?
            </div>
          )}

          {/* Mobile cards. */}
          <div className="divide-y divide-border-muted md:hidden">
            {users.data?.map((u) => {
              const isMe = me?.id === u.id;
              const eligible =
                projects.data?.filter((proj) => !u.projects.some((m) => m.project_id === proj.id)) ?? [];
              return (
                <div key={u.id} className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-medium">{u.username}</span>
                      {isMe && (
                        <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] uppercase text-accent">you</span>
                      )}
                    </div>
                    {u.is_superuser ? (
                      <span className="pill shrink-0 bg-accent/15 text-accent">admin</span>
                    ) : (
                      <span className="pill shrink-0 bg-status-pending/15 text-text-muted">contributor</span>
                    )}
                  </div>
                  <div className="text-sm text-text-muted">
                    {u.display_name ?? "—"}
                    {u.is_active === false && <span className="ml-2 text-status-warn">· inactive</span>}
                  </div>
                  <div>
                    {u.projects.length === 0 ? (
                      <span className="text-xs text-text-dim">No projects</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {u.projects.map((p) => (
                          <span
                            key={p.project_id}
                            className={cn(
                              "pill text-[10px]",
                              p.role === "owner" ? "bg-accent/15 text-accent" : "bg-bg-elevated text-text-muted",
                            )}
                          >
                            {p.project_name}
                            {p.role === "owner" && " (owner)"}
                          </span>
                        ))}
                      </div>
                    )}
                    {addingForUserId === u.id ? (
                      <div className="mt-2 flex items-center gap-2">
                        <select
                          className="input h-9 py-0 text-sm"
                          defaultValue=""
                          autoFocus
                          onChange={(e) => {
                            const projectId = Number(e.target.value);
                            if (!projectId) return;
                            addToProject.mutate({ projectId, username: u.username });
                          }}
                        >
                          <option value="" disabled>Pick a project…</option>
                          {eligible.map((proj) => (
                            <option key={proj.id} value={proj.id}>{proj.name}</option>
                          ))}
                        </select>
                        <button type="button" className="text-xs text-text-dim hover:text-text" onClick={() => setAddingForUserId(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      eligible.length > 0 && (
                        <button
                          type="button"
                          className="mt-1.5 inline-flex items-center gap-1 text-xs text-text-dim hover:text-accent"
                          onClick={() => setAddingForUserId(u.id)}
                        >
                          <Plus size={12} /> Add to project
                        </button>
                      )
                    )}
                  </div>
                  <button
                    type="button"
                    className={cn("btn-ghost w-full text-sm", isMe && "cursor-not-allowed opacity-50")}
                    disabled={isMe || toggleAdmin.isPending}
                    onClick={() => toggleAdmin.mutate({ id: u.id, makeAdmin: !u.is_superuser })}
                  >
                    {u.is_superuser ? (<><ShieldOff size={14} /> Demote to contributor</>) : (<><ShieldCheck size={14} /> Promote to admin</>)}
                  </button>
                </div>
              );
            })}
            {users.data?.length === 0 && (
              <div className="p-10 text-center text-sm text-text-muted">No users yet.</div>
            )}
          </div>

          {/* Desktop table — unchanged at md+. */}
          <div className="hidden md:block">
          {(users.data?.length ?? 0) > 0 && (
          <div className="data-row grid-cols-[2fr_1.5fr_3fr_1fr_1fr_1fr] text-xs font-semibold uppercase tracking-wider text-text-dim">
            <div>Username</div>
            <div>Display name</div>
            <div>Projects</div>
            <div>Role</div>
            <div>Status</div>
            <div className="text-right">Action</div>
          </div>
          )}
          {users.data?.map((u) => {
            const isMe = me?.id === u.id;
            return (
              <div
                key={u.id}
                className="data-row grid-cols-[2fr_1.5fr_3fr_1fr_1fr_1fr]"
              >
                <div className="flex items-center gap-2 font-medium">
                  {u.username}
                  {isMe && (
                    <span className="rounded bg-accent/15 px-1.5 py-0.5 text-[10px] uppercase text-accent">
                      you
                    </span>
                  )}
                </div>
                <div className="text-text-muted">{u.display_name ?? "—"}</div>
                <div>
                  {u.projects.length === 0 ? (
                    <span className="text-xs text-text-dim">none</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.projects.map((p) => (
                        <span
                          key={p.project_id}
                          className={cn(
                            "pill text-[10px]",
                            p.role === "owner"
                              ? "bg-accent/15 text-accent"
                              : "bg-bg-elevated text-text-muted",
                          )}
                          title={`${p.role} of ${p.project_name}`}
                        >
                          {p.project_name}
                          {p.role === "owner" && " (owner)"}
                        </span>
                      ))}
                    </div>
                  )}
                  {addingForUserId === u.id ? (
                    <div className="mt-2 flex items-center gap-2">
                      <select
                        className="input h-7 py-0 text-xs"
                        defaultValue=""
                        autoFocus
                        onChange={(e) => {
                          const projectId = Number(e.target.value);
                          if (!projectId) return;
                          addToProject.mutate({ projectId, username: u.username });
                        }}
                      >
                        <option value="" disabled>
                          Pick a project…
                        </option>
                        {projects.data
                          ?.filter(
                            (proj) =>
                              !u.projects.some((m) => m.project_id === proj.id),
                          )
                          .map((proj) => (
                            <option key={proj.id} value={proj.id}>
                              {proj.name}
                            </option>
                          ))}
                      </select>
                      <button
                        type="button"
                        className="text-xs text-text-dim hover:text-text"
                        onClick={() => setAddingForUserId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    (() => {
                      const eligible =
                        projects.data?.filter(
                          (proj) =>
                            !u.projects.some((m) => m.project_id === proj.id),
                        ) ?? [];
                      if (eligible.length === 0) return null;
                      return (
                        <button
                          type="button"
                          className="mt-1 inline-flex items-center gap-1 text-[10px] text-text-dim hover:text-accent"
                          onClick={() => setAddingForUserId(u.id)}
                          title="Add to project"
                        >
                          <Plus size={10} /> Add to project
                        </button>
                      );
                    })()
                  )}
                </div>
                <div>
                  {u.is_superuser ? (
                    <span className="pill bg-accent/15 text-accent">admin</span>
                  ) : (
                    <span className="pill bg-status-pending/15 text-text-muted">contributor</span>
                  )}
                </div>
                <div>
                  {u.is_active !== false ? (
                    <span className="text-xs text-text-muted">active</span>
                  ) : (
                    <span className="text-xs text-status-warn">inactive</span>
                  )}
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    className={cn(
                      "btn-ghost text-xs",
                      isMe && "cursor-not-allowed opacity-50",
                    )}
                    disabled={isMe || toggleAdmin.isPending}
                    title={
                      isMe
                        ? "You can't change your own admin role"
                        : u.is_superuser
                          ? "Demote to contributor"
                          : "Promote to admin"
                    }
                    onClick={() =>
                      toggleAdmin.mutate({ id: u.id, makeAdmin: !u.is_superuser })
                    }
                  >
                    {u.is_superuser ? (
                      <>
                        <ShieldOff size={14} /> Demote
                      </>
                    ) : (
                      <>
                        <ShieldCheck size={14} /> Promote
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
          {users.data?.length === 0 && (
            <div className="p-10 text-center text-sm text-text-muted">No users yet.</div>
          )}
          </div>
        </div>
      </div>
    </>
  );
}
